import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, http } from '@/api/client'
import type { StoredFile } from '@/features/students/types'
import type { ApiItem, ListParams } from '@/types/api'
import type { LeaveRequestFormOutput, LeaveTypeFormOutput } from './schemas'
import type {
  LeaveBalance,
  LeaveCalendarData,
  LeaveCounts,
  LeaveKind,
  LeaveRequest,
  LeaveType,
} from './types'

export const leaveKeys = {
  all: ['leave'] as const,
  types: () => [...leaveKeys.all, 'types'] as const,
  typeOptions: (kind: LeaveKind) => [...leaveKeys.all, 'type-options', kind] as const,
  list: (params: object) => [...leaveKeys.all, 'requests', params] as const,
  counts: () => [...leaveKeys.all, 'counts'] as const,
  detail: (id: number) => [...leaveKeys.all, 'detail', id] as const,
  calendar: (from: string, to: string) => [...leaveKeys.all, 'calendar', from, to] as const,
  balances: (kind: LeaveKind, personId: number) => [...leaveKeys.all, 'balances', kind, personId] as const,
}

/* ---------- Admin ---------- */

export type LeaveListParams = ListParams & { status?: string; kind?: string; typeId?: string }

export const leaveRequestsQuery = (params: LeaveListParams) =>
  queryOptions({
    queryKey: leaveKeys.list(params),
    queryFn: () => api.page<LeaveRequest>('/leave/requests', params),
    placeholderData: keepPreviousData,
  })
export const leaveCountsQuery = () =>
  queryOptions({
    queryKey: leaveKeys.counts(),
    queryFn: () => api.get<LeaveCounts>('/leave/requests/counts'),
  })
export const leaveRequestQuery = (id: number) =>
  queryOptions({
    queryKey: leaveKeys.detail(id),
    queryFn: () => api.get<LeaveRequest>(`/leave/requests/${id}`),
  })
export const leaveTypesQuery = () =>
  queryOptions({ queryKey: leaveKeys.types(), queryFn: () => api.get<LeaveType[]>('/leave/types') })
export const leaveTypeOptionsQuery = (kind: LeaveKind) =>
  queryOptions({
    queryKey: leaveKeys.typeOptions(kind),
    queryFn: () => api.get<LeaveType[]>('/leave/types/options', { params: { kind } }),
  })
export const leaveCalendarQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: leaveKeys.calendar(from, to),
    queryFn: () => api.get<LeaveCalendarData>('/leave/calendar', { params: { from, to } }),
    placeholderData: keepPreviousData,
  })
export const personBalancesQuery = (kind: LeaveKind, personId: number) =>
  queryOptions({
    queryKey: leaveKeys.balances(kind, personId),
    queryFn: () => api.get<LeaveBalance[]>('/leave/balances', { params: { kind, personId } }),
  })

function useLeaveMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leaveKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useDecideLeave = () =>
  useLeaveMutation(
    ({ id, decision, note }: { id: number; decision: 'approve' | 'reject'; note: string | null }) =>
      api.post<LeaveRequest>(`/leave/requests/${id}/${decision}`, { note }),
  )
export const useAdminCancelLeave = () =>
  useLeaveMutation((id: number) => api.post<LeaveRequest>(`/leave/requests/${id}/cancel`))
export const useFileLeaveOnBehalf = () =>
  useLeaveMutation(
    (
      payload: LeaveRequestFormOutput & { kind: LeaveKind; staffId: number | null; studentId: number | null },
    ) => api.post<LeaveRequest>('/leave/requests', payload),
  )
export const useSaveLeaveType = () =>
  useLeaveMutation(({ id, ...payload }: LeaveTypeFormOutput & { id?: number }) =>
    id ? api.put<LeaveType>(`/leave/types/${id}`, payload) : api.post<LeaveType>('/leave/types', payload),
  )
export const useDeleteLeaveType = () => useLeaveMutation((id: number) => api.delete(`/leave/types/${id}`))

/* ---------- Portals (staff: own record; family: one student) ---------- */

export interface LeaveScope {
  /** API base, e.g. /me/staff/leave; also part of the query keys. */
  base: string
}

export const staffLeaveScope: LeaveScope = { base: '/me/staff/leave' }
export const familyLeaveScope = (studentId: number): LeaveScope => ({
  base: `/me/family/students/${studentId}/leave`,
})

export const scopeTypesQuery = (s: LeaveScope) =>
  queryOptions({
    queryKey: [...leaveKeys.all, 'me', s.base, 'types'],
    queryFn: () => api.get<LeaveType[]>(`${s.base}/types`),
  })
export const scopeBalancesQuery = (s: LeaveScope) =>
  queryOptions({
    queryKey: [...leaveKeys.all, 'me', s.base, 'balances'],
    queryFn: () => api.get<LeaveBalance[]>(`${s.base}/balances`),
  })
export const scopeRequestsQuery = (s: LeaveScope) =>
  queryOptions({
    queryKey: [...leaveKeys.all, 'me', s.base, 'requests'],
    queryFn: () => api.get<LeaveRequest[]>(`${s.base}/requests`),
  })

export const useSubmitLeave = (s: LeaveScope) =>
  useLeaveMutation((payload: LeaveRequestFormOutput) => api.post<LeaveRequest>(`${s.base}/requests`, payload))
export const useCancelLeave = (s: LeaveScope) =>
  useLeaveMutation((id: number) => api.post<LeaveRequest>(`${s.base}/requests/${id}/cancel`))

/** Uploads a private supporting document; open to anyone who may request or manage leave. */
export function useUploadLeaveAttachment() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await http.post<ApiItem<StoredFile>>('/leave/attachments', form)
      return res.data.data
    },
  })
}
